import { createColumns } from './column';
import { useDeleteOrganisationMediasMutation } from '@/services/organisations/queries';
import { showToast } from '@/components/ui/show-toast';
import { Button } from '@/components/ui/button';
import { Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table';

type MediaTableProps = {
  data: any[];
  totalItems: number;
  organisationId?: string;
};

const MediaTable = (prop: MediaTableProps) => {
  const { data, organisationId } = prop;
  const [rowSelection, setRowSelection] = useState({});

  const { mutate: deleteMedia, isPending: isDeleting } = useDeleteOrganisationMediasMutation(
    organisationId || '',
    () => {
      const selectedCount = Object.keys(rowSelection).length;
      showToast('success', `Successfully deleted ${selectedCount} media file(s)`);
      setRowSelection({});
    },
    () => {
      showToast('error', 'Failed to delete selected media files. Please try again.');
    }
  );

  const handleBulkDelete = () => {
    if (!organisationId) {
      showToast('error', 'Organization data not available');
      return;
    }

    const selectedRowIndices = Object.keys(rowSelection);
    if (selectedRowIndices.length === 0) {
      showToast('error', 'Please select media files to delete');
      return;
    }

    // Get URLs from selected media items
    const selectedRows = selectedRowIndices.map(index => data[parseInt(index)]);
    const mediaUrls = selectedRows.map(row => row.url || row.file?.url).filter(Boolean);
    
    if (mediaUrls.length === 0) {
      showToast('error', 'No valid media URLs found in selected items');
      return;
    }

    deleteMedia(mediaUrls);
  };

  const columns = createColumns(organisationId);

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
    enableRowSelection: true,
  });

  const selectedCount = Object.keys(rowSelection).length;

  return (
    <div className='w-full space-y-4'>
      {selectedCount > 0 && (
        <div className='flex items-center justify-between bg-red-50 p-3 rounded-md border border-red-200'>
          <span className='text-sm text-red-700'>
            {selectedCount} media file(s) selected
          </span>
          <Button
            variant='destructive'
            size='sm'
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className='flex items-center gap-2'
          >
            <Trash2Icon className='h-4 w-4' />
            {isDeleting ? 'Deleting...' : 'Delete Selected'}
          </Button>
        </div>
      )}
      
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className='h-24 text-center'>
                  No media files found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default MediaTable;
